import axiosInstance from "../../common/axiosInstance";

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface ReportTypeConfig {
  key: string;
  label: string;
  description: string;
  apiEndpoint: string;
}

export interface MonthAvailability {
  month: number; // 1–12
  totalCount: number;
  breakdown: {
    misReports: number;
    fireSafetyScorecard: number;
    electricalScorecard: number;
    splitAc: number;
    kpiScorecard: number;
  };
}

// ─── Month Utilities ─────────────────────────────────────────────────────────

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const getMonthName = (month: number): string => MONTH_NAMES[month - 1] ?? "";

export const getMonthFromDate = (dateString: string): number => {
  if (!dateString) return 0;
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return 0;
  return d.getMonth() + 1; // 1-indexed
};

// ─── Report Types ─────────────────────────────────────────────────────────────

export const REPORT_TYPES: ReportTypeConfig[] = [
  {
    key: "mis-reports",
    label: "MIS Reports",
    description: "Monthly Information System fire safety checklists",
    apiEndpoint: "/api/fire-safety-mis-checklists",
  },
  {
    key: "fire-safety-scorecard",
    label: "Fire Safety Scorecard",
    description: "Fire safety parameter scoring and audit records",
    apiEndpoint: "/api/fire-safety-scorecards",
  },
  {
    key: "electrical-scorecard",
    label: "Electrical Scorecard",
    description: "Electrical safety compliance and inspection scores",
    apiEndpoint: "/api/electrical-safety-scorecards",
  },
  {
    key: "split-ac",
    label: "Split AC Checklist",
    description: "Split AC unit maintenance and inspection records",
    apiEndpoint: "/api/split-ac-checklist",
  },
  {
    key: "kpi-scorecard",
    label: "KPI Scorecard",
    description: "Key performance indicator assessments",
    apiEndpoint: "/api/kpi/assessments",
  },
  {
    key: "equipment-inspections",
    label: "Equipment Inspections",
    description: "Fire safety equipment inspection records by equipment type",
    apiEndpoint: "/api/inspection-assessments/monitoring/by-premises",
  },
];

export const getReportTypeConfig = (reportType: string): ReportTypeConfig | undefined =>
  REPORT_TYPES.find((rt) => rt.key === reportType);

// ─── Premises Utilities ───────────────────────────────────────────────────────

export const getPremiseName = (
  premiseId: string,
  allPremises: any[]
): string => {
  const premise = allPremises.find(
    (p) => (p._id || p.id) === premiseId
  );
  return premise?.premisesName ?? premiseId;
};

// ─── Safe API Count ───────────────────────────────────────────────────────────

export const safeCount = async (
  endpoint: string,
  params: Record<string, any>
): Promise<number> => {
  try {
    const res = await axiosInstance.get(endpoint, { params });
    // Handle paginated response (e.g. KPI assessments returns { data: [], meta: { count } })
    if (res.data?.meta?.count !== undefined) {
      return res.data.meta.count;
    }

    const data = res.data?.data || res.data || res.data?.data?.inspections || [];

    // return Array.isArray(data) ? data.length : 0;
    return typeof data === 'object' && data !== null && 'totalInspections' in data ? data.totalInspections : Array.isArray(data) ? data.length : 0;

  } catch {
    return 0;
  }
};

// ─── Safe Fetch All ───────────────────────────────────────────────────────────

export const safeFetchAll = async (
  endpoint: string,
  params: Record<string, any>
): Promise<any[]> => {
  try {
    // Use a high limit to get all records for availability grouping
    const res = await axiosInstance.get(endpoint, { params: { ...params, limit: 2000 } });
    const data = res.data?.data || res.data || [];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};
